import { useState } from 'react';
import { RiShieldCheckLine } from 'react-icons/ri';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useStore } from '../state/store';
import {
  CLASSIFICATION_LABEL,
  PRIORITY_LABEL,
  type Classification,
  type Priority,
} from '../state/domain';
import { cn } from '@/lib/utils';

const EMPTY = {
  name: '',
  caseNo: '',
  client: '',
  purpose: '',
  legalBasis: '',
  classification: 'internal' as Classification,
  priority: 'normal' as Priority,
  lead: '',
  authorizedBy: '',
  expiresAt: '',
  domains: '',
  ips: '',
  emails: '',
  orgs: '',
};

export function CaseDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createEngagement = useStore(state => state.createEngagement);
  const [ack, setAck] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const toList = (value: string) =>
    value
      .split(/[,，\n\s]+/)
      .map(v => v.trim())
      .filter(Boolean);

  const submit = async () => {
    if (!ack || !form.name.trim()) return;
    await createEngagement({
      name: form.name.trim(),
      caseNo: form.caseNo || undefined,
      client: form.client,
      purpose: form.purpose,
      legalBasis: form.legalBasis,
      classification: form.classification,
      priority: form.priority,
      lead: form.lead,
      authorizedBy: form.authorizedBy,
      expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : undefined,
      scope: {
        domains: toList(form.domains),
        ips: toList(form.ips),
        emails: toList(form.emails),
        orgs: toList(form.orgs),
      },
    });
    setForm(EMPTY);
    setAck(false);
    onClose();
  };

  const field = (
    key: keyof typeof EMPTY,
    label: string,
    placeholder?: string,
    type = 'text'
  ) => (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <Input
        type={type}
        value={form[key]}
        placeholder={placeholder}
        onChange={e => setForm({ ...form, [key]: e.target.value })}
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={value => !value && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>立案 · 新建授权委托</DialogTitle>
          <DialogDescription>
            每次分析必须先登记合法授权与检索范围，系统据此执行授权门校验、范围限制与全程留痕。
          </DialogDescription>
        </DialogHeader>

        <div className="mb-4 flex gap-2.5 rounded-[var(--radius)] border border-primary/25 bg-primary/5 p-3">
          <RiShieldCheckLine className="mt-0.5 size-4 shrink-0 text-primary" />
          <div className="flex flex-col gap-2">
            <p className="text-[11.5px] leading-relaxed text-muted-foreground">
              仅用于已获得书面授权的防御性评估：红队演练、自有资产暴露面自查、安全意识培训复盘。
              禁止用于未授权的个人画像、跟踪、骚扰或任何违法行为。
            </p>
            <label className="flex cursor-pointer items-start gap-2 text-[11.5px]">
              <Checkbox checked={ack} onCheckedChange={value => setAck(Boolean(value))} className="mt-0.5" />
              <span>我确认本次分析已获得合法书面授权，且全部检索目标落在下列范围内。</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">{field('name', '案件名称 *', '例：自有资产外部暴露面自查')}</div>
          {field('caseNo', '案号', '例：CASE-2026-0001')}
          {field('client', '委托方', '例：内部安全团队')}
          {field('legalBasis', '授权依据 / 编号', '例：AUTH-2026-001')}
          {field('authorizedBy', '授权人')}
          {field('lead', '主办人')}
          {field('expiresAt', '授权到期', undefined, 'date')}
          <div className="flex flex-col gap-1.5">
            <Label>密级</Label>
            <select
              className="h-8 w-full rounded-[var(--radius-sm)] border border-input bg-card px-2 text-[12px]"
              value={form.classification}
              onChange={e => setForm({ ...form, classification: e.target.value as Classification })}
            >
              {(Object.keys(CLASSIFICATION_LABEL) as Classification[]).map(level => (
                <option key={level} value={level}>
                  {CLASSIFICATION_LABEL[level]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>优先级</Label>
            <select
              className="h-8 w-full rounded-[var(--radius-sm)] border border-input bg-card px-2 text-[12px]"
              value={form.priority}
              onChange={e => setForm({ ...form, priority: e.target.value as Priority })}
            >
              {(Object.keys(PRIORITY_LABEL) as Priority[]).map(level => (
                <option key={level} value={level}>
                  {PRIORITY_LABEL[level]}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2">{field('purpose', '分析目的')}</div>
          <div className="col-span-2">
            {field('domains', '授权域名（逗号分隔，支持 * 通配）', 'example.com, *.example.com')}
          </div>
          <div className="col-span-2">{field('ips', '授权 IP / 网段', '203.0.113.10')}</div>
          <div className="col-span-2">{field('emails', '授权邮箱', '*@example.com')}</div>
          <div className="col-span-2">{field('orgs', '授权组织（名称，用于范围记录）')}</div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
          <Button
            disabled={!ack || !form.name.trim()}
            onClick={submit}
            className={cn(!ack || !form.name.trim() ? 'opacity-50' : '')}
          >
            立案并开始
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}